import React, { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAllContacts, selectContactGetter } from '@actions/contacts';
import { ContactPreviewSidebar } from '@components/Contact/Sidebar';
import Container from '@components/ContentContainer';
import Contact from '@models/Contact';
import { useTheme } from '@support/hooks/use-theme';
import { PeopleHeader } from '@views/People';
import Header from '@components/Header';
import styles from './Map.module.scss';
import { useFullContactSearch } from '@support/hooks/use-contact-search';
import { useSaveViewState } from '@support/hooks/use-save-view-state';
import { selectSavedViewState, selectSelectedContact, setActiveContact } from '@actions/ui';
import SidebarList from './SidebarList';
import SVGWrapper from '@components/Icons/SVGWrapper';
import { ClusterID, ContactID } from './Annotations';
import ClayMapkit from './ClayMapkit';
import { MapViewState } from '@reducers/ui/viewRestoration';

type MapDisplayState = Omit<MapViewState, 'visibleMapRect'>;
type MapDisplayAction =
  | ['selectMapContact', ContactID]
  | ['selectMapCluster', ClusterID, ContactID[], ContactID | undefined];

const mapDisplayStateReducer = (
  state: MapDisplayState,
  action: MapDisplayAction,
): MapDisplayState => {
  switch (action[0]) {
    case 'selectMapContact':
      return {
        selectedContactID: action[1],
        selectedClusterID: null,
        selectedClusterContactIDs: null,
        selectedClusterSelectedContactID: null,
      };

    case 'selectMapCluster':
      return {
        selectedContactID: null,
        selectedClusterID: action[1],
        selectedClusterContactIDs: action[2],
        selectedClusterSelectedContactID: action[3] || null,
      };

    default:
      return state;
  }
};

const viewID = 'Map';

function MapDisplay({ contacts }: { contacts: Contact[] }) {
  const [theme] = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ClayMapkit | null>(null);
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const appLevelSelectedContact = useSelector(selectSelectedContact);
  const contactGetter = useSelector(selectContactGetter);
  const [
    {
      selectedContactID,
      selectedClusterID,
      selectedClusterContactIDs,
      selectedClusterSelectedContactID,
    },
    dispatchToMapDisplay,
  ] = useReducer(mapDisplayStateReducer, {
    selectedContactID: savedViewState?.selectedContactID || null,
    selectedClusterID: savedViewState?.selectedClusterID || null,
    selectedClusterContactIDs: savedViewState?.selectedClusterContactIDs || null,
    selectedClusterSelectedContactID: savedViewState?.selectedClusterSelectedContactID || null,
  });
  const selectedContact = contactGetter(selectedContactID);

  // Using the ref allows us to pass this state to a handler utilized by the mapkit instance
  const selectionRef = useRef<{
    selectedContactID: ContactID | null;
    selectedClusterID: ClusterID | null;
  }>({ selectedContactID, selectedClusterID });

  useSaveViewState(
    viewID,
    {
      selectedContactID,
      selectedClusterID,
      selectedClusterContactIDs,
      selectedClusterSelectedContactID: selectedClusterID ? appLevelSelectedContact?.id : null,
      visibleMapRect: mapRef.current?.map.visibleMapRect,
    },
    [
      selectedContactID,
      selectedClusterID,
      appLevelSelectedContact,
      mapRef.current?.map.visibleMapRect,
    ],
  );

  const highlightSelectedAnnotation = () => {
    const { selectedContactID, selectedClusterID } = selectionRef.current;
    mapContainerRef.current
      ?.querySelector('.selected-annotation')
      ?.classList.remove('selected-annotation');

    if (mapContainerRef.current && selectedContactID) {
      mapContainerRef.current
        .querySelector(`.contact-${selectedContactID}`)
        ?.classList.add('selected-annotation');
    } else if (mapContainerRef.current && selectedClusterID) {
      mapContainerRef.current
        .querySelector(`.cluster-${selectedClusterID}`)
        ?.classList.add('selected-annotation');
    }
  };

  useEffect(() => {
    if (selectedContact) {
      dispatch(setActiveContact(selectedContact, 'map-annotation'));
    }
  }, [selectedContact]);

  useEffect(() => {
    selectionRef.current = { selectedContactID, selectedClusterID };
  }, [selectedContactID, selectedClusterID]);

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new ClayMapkit({
        theme: theme,
        mapContainerNode: mapContainerRef.current,
        visibleMapRect: savedViewState?.visibleMapRect || null,
        selectContact: (contactID: ContactID) =>
          dispatchToMapDisplay(['selectMapContact', contactID]),
        selectCluster: (clusterID: ClusterID, contactIDs: ContactID[]) => {
          if (selectionRef.current.selectedClusterID === clusterID) {
            return;
          }
          dispatchToMapDisplay(['selectMapCluster', clusterID, contactIDs, null]);
        },
        navigate,
        onZoom: () => dispatchToMapDisplay(['selectMapCluster', null, null, null]),
        onRegionChange: highlightSelectedAnnotation,
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.cleanup();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.map.colorScheme = theme === 'light' ? 'light' : 'dark';
    }
  }, [theme]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.updateNavigate(navigate);
    }
  }, [navigate]);

  useEffect(() => {
    if (mapRef.current && contacts.length > 0) {
      mapRef.current.updateContacts(contacts);
    }
  }, [contacts.length]);

  useEffect(highlightSelectedAnnotation, [selectedClusterID, selectedContactID]);

  useEffect(() => {
    if (mapContainerRef.current) {
      const observer = new MutationObserver((mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
              if (
                node.nodeType === Node.ELEMENT_NODE &&
                node instanceof HTMLDivElement &&
                node.classList.contains('annotations-loaded')
              ) {
                highlightSelectedAnnotation();
                observer.disconnect();
                break;
              }
            }
          }
        }
      });

      observer.observe(mapContainerRef.current, { childList: true, subtree: true });
      return () => observer.disconnect();
    }

    return () => {};
  }, []);

  return (
    <Container>
      <Container.Primary
        id="content"
        header={
          <Header className={styles.listHeader} actions={false}>
            <PeopleHeader />
          </Header>
        }
      >
        <div
          ref={mapContainerRef}
          style={{ width: '100%', height: '100%' }}
          className={styles.mapContainer}
        >
          <div className={styles.zoomControls}>
            <button
              onClick={() => mapRef.current?.zoomIn()}
              disabled={mapRef.current?.zoomInDisabled}
            >
              <SVGWrapper width="18" height="18" viewBox="0 0 18 18" color="primary">
                <path
                  fill="none"
                  strokeLinecap="round"
                  strokeWidth="1.5"
                  d="M3.75 9h10.5M9 14.25V3.75"
                />
              </SVGWrapper>
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut()}
              disabled={mapRef.current?.zoomOutDisabled}
            >
              <SVGWrapper width="18" height="18" viewBox="0 0 18 18" color="primary">
                <path fill="none" strokeLinecap="round" strokeWidth="1.5" d="M3.75 9h10.5" />
              </SVGWrapper>
            </button>
          </div>
        </div>
      </Container.Primary>
      <ContactPreviewSidebar
        contactID={selectedContactID}
        nonContactPreviewClassName={styles.contactListSidebar}
      >
        {selectedClusterContactIDs && selectedClusterContactIDs.length > 0 && (
          <SidebarList
            key={selectedClusterID}
            contactIDs={selectedClusterContactIDs}
            defaultSelectedContactID={selectedClusterSelectedContactID}
          />
        )}
      </ContactPreviewSidebar>
    </Container>
  );
}

export default function Map() {
  const contacts = useSelector(selectAllContacts);
  useFullContactSearch({ term: 'has:coordinates' });

  return <MapDisplay contacts={contacts.valueSeq().toArray()} />;
}
